import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('why-email-goes-to-spam');

/**
 * PILLAR of the deliverability cluster. The differentiator is ORDERING: every competing article
 * on this query is an undifferentiated list of twenty things, which is useless to someone with a
 * live problem. This one is ranked by how often each cause is actually to blame, and says so.
 */
export default function WhyEmailGoesToSpamPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Mail lands in spam for five reasons, and they are not equally likely. In rough order: authentication is failing, your sending reputation is poor, your list contains addresses that should not be there, recipients are ignoring you, or the message itself trips filters. Content is last, and it is where almost everyone starts.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Work down the list, not up it">
        <p>
          The instinct when mail starts landing in spam is to rewrite the copy. That is the fifth
          thing to check, and changing it while one of the first four is broken produces no
          improvement and no information — you have changed a variable that was not the cause.
        </p>
        <LearnTable
          head={['#', 'Cause', 'How often it is the real one', 'Time to fix']}
          rows={[
            ['1', 'Authentication failing', 'Very common, and usually invisible', 'Hours'],
            ['2', 'Poor sending reputation', 'Common', 'Weeks'],
            ['3', 'List quality', 'Common, and the usual cause of #2', 'Days'],
            ['4', 'Low engagement', 'Common on older lists', 'Weeks to months'],
            ['5', 'Message content', <Lead key="a">Rarely the primary cause</Lead>, 'Minutes'],
          ]}
        />
        <p>
          The second column is the part other guides omit. All five are real, but a filter weighs
          them very differently, and so should you.
        </p>
      </LearnSection>

      <LearnSection title="1. Authentication is failing">
        <p>
          The one that is both most likely and least visible, because nothing in your sending tool
          reports it. Mail delivers, it just delivers into spam.
        </p>
        <p>
          Since February 2024 this is no longer a soft signal at the two largest providers: Gmail
          and Yahoo require SPF or DKIM below 5,000 messages a day to that provider, and both plus
          an aligned DMARC record above it. Failing is not a ranking penalty; it is a condition of
          delivery.
        </p>
        <p>
          Two failure modes account for most of it. Either the records were never right, or —
          more insidious — SPF passes for your ESP’s bounce domain rather than yours, so DMARC
          discards the pass as{' '}
          <Link to="/learn/dmarc-alignment" className="text-accent underline underline-offset-4">
            unaligned
          </Link>
          . Start at{' '}
          <Link to="/learn/email-authentication" className="text-accent underline underline-offset-4">
            how the three records fit together
          </Link>{' '}
          and confirm all three before touching anything else.
        </p>
      </LearnSection>

      <LearnSection title="2. Your sending reputation is poor">
        <p>
          Providers score you continuously, and the score is what decides borderline messages. It is
          also slow: reputation is earned over weeks and lost in a day.
        </p>
        <p>
          The distinction that matters, and that most senders never learn, is that{' '}
          <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
            domain and IP reputation are separate scores
          </Link>
          . On a shared ESP IP you own the domain score and have almost no influence over the IP
          one. Blaming the wrong half is why people migrate ESPs and see no change.
        </p>
        <p>
          Two things move it fastest in the wrong direction: complaint rate above 0.3% (Gmail’s
          stated ceiling), and{' '}
          <Link to="/learn/email-blacklist" className="text-accent underline underline-offset-4">
            a blacklist entry
          </Link>{' '}
          on a list receivers actually consult.
        </p>
      </LearnSection>

      <LearnSection title="3. Your list contains addresses that should not be there">
        <p>
          Usually the cause of number two rather than a separate problem. Three categories, in
          descending order of damage:
        </p>
        <LearnList>
          <li>
            <Lead>Spam traps.</Lead> Addresses that never opted in to anything — either recycled
            (a real address abandoned and repurposed) or pristine (never used by a human). Hitting a
            pristine trap is close to conclusive evidence of purchased or scraped data, and the
            reputation damage is severe.
          </li>
          <li>
            <Lead>Dead addresses.</Lead> Hard bounces above roughly 2% signal to a provider that you
            are not maintaining your list. The bounce itself is harmless; the pattern is not.
          </li>
          <li>
            <Lead>People who never asked.</Lead> Purchased lists, scraped addresses, and the
            conference badge scan from 2023. These generate complaints, and complaints outweigh
            almost everything else.
          </li>
        </LearnList>
        <p>
          There is no content fix for any of these. If your list is the problem, the message is
          irrelevant.
        </p>
      </LearnSection>

      <LearnSection title="4. Nobody is engaging">
        <p>
          Gmail in particular weighs recipient behaviour heavily — opens, replies, moving a message
          out of spam, deleting without reading. A list that never engages teaches the filter that
          your mail is unwanted, even when every technical signal is perfect.
        </p>
        <p>
          The counter-intuitive fix is to send to <em>fewer</em> people. Suppress anyone who has not
          opened in six months and your engagement rate rises immediately, which is the signal the
          filter reads. Most senders do the opposite when results dip.
        </p>
      </LearnSection>

      <LearnSection title="5. The message itself">
        <p>
          Last, and genuinely last. Modern filters weigh sender reputation far above vocabulary, and
          the widely-copied lists of forbidden words mostly date from an era when that was reversed.
        </p>
        <p>
          What still matters is structural rather than lexical:
        </p>
        <LearnList>
          <li>A single large image with almost no text — historically a spammer technique to defeat text analysis</li>
          <li>Link shorteners, which hide the destination from the filter</li>
          <li>A sending domain that differs from every link domain in the body</li>
          <li>A missing or broken unsubscribe — and above 5,000/day, a missing one-click header is a hard requirement</li>
          <li>HTML that no mail client would generate, which suggests it was assembled to evade parsing</li>
        </LearnList>
        <p>
          Word choice is well down that list. Our{' '}
          <Link to="/tools/spam-words-checker" className="text-accent underline underline-offset-4">
            spam-words checker
          </Link>{' '}
          exists and is useful for a sanity pass, and it will not rescue a domain with a reputation
          problem.
        </p>
      </LearnSection>

      <LearnSection title="Diagnosing yours in about twenty minutes">
        <LearnSubsection title="Confirm authentication, then stop guessing">
          <p>
            Send yourself a message and read the raw headers. <Tok>Authentication-Results</Tok>{' '}
            tells you SPF, DKIM and DMARC verdicts in one line, from the receiver’s own perspective.
            If any of them is not <Tok>pass</Tok>, you have found your problem and the rest of this
            list is premature.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Check whether it is you or the list">
          <p>
            Send the same message to a fresh personal address at Gmail, Outlook and Yahoo. Inbox
            everywhere means the message is fine and the problem is your list or your reputation
            with those specific recipients. Spam everywhere means it is you.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Check whether it is one provider or all">
          <p>
            Spam at Gmail but inbox at Outlook is an engagement or reputation problem specific to
            Gmail — they weigh recipient behaviour hardest. Spam everywhere is authentication,
            a blacklist, or list quality.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Only then, change the message">
          <p>
            And change one thing at a time. Rewriting subject, body and sending domain together
            means you learn nothing from the result.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is the single most common cause?',
    a: 'Authentication that is failing without anyone noticing. It produces no error, no bounce and no warning in your sending tool — mail delivers, it just delivers into the spam folder. It is also the fastest of the five to fix.',
  },
  {
    q: 'Do spam trigger words actually matter?',
    a: 'Far less than the published lists suggest. Modern filters weigh sender reputation, authentication and recipient engagement well above vocabulary, and most circulated word lists are copies of a list that was already dated a decade ago.',
  },
  {
    q: 'Why does Gmail put me in spam when Outlook does not?',
    a: 'Gmail weighs recipient engagement more heavily than any other provider. A list that never opens or replies teaches Gmail specifically that your mail is unwanted, even when every technical signal is correct. Outlook leans harder on IP reputation.',
  },
  {
    q: 'I bought a list and now everything goes to spam. Can I fix it?',
    a: 'Not by fixing the mail. Purchased lists contain spam traps and people who never opted in, and both damage your reputation in ways no amount of content or authentication work recovers. Stop sending to it and rebuild reputation on a list that opted in.',
  },
  {
    q: 'How long does it take to recover?',
    a: 'Authentication fixes take effect within hours, once DNS propagates. Reputation recovery takes weeks of consistent low-complaint sending to engaged recipients. There is no way to accelerate the second, and services claiming otherwise are selling volume, not recovery.',
  },
  {
    q: 'Does the spam folder mean I was blacklisted?',
    a: 'Not usually. Blacklists are one input among many, and most spam placement happens without any listing at all. Check anyway, because a listing on a list receivers actually consult is worth knowing about, but do not assume it is the cause.',
  },
];
